import React, { useState, useCallback, memo } from 'react';
import Popover from './Popover.jsx';
import ContextMenu from './ContextMenu.jsx';
import ExplainModal from './ExplainModal.jsx';
import ProvenanceChip from './ProvenanceChip.jsx';
import { useToast } from './Toast.jsx';
import { fmtMoney, lookupRegistry } from '../engine.js';
import { GLOSSARY } from '../data.js';
import { useGuides } from './GuidesContext.jsx';
import { formatMoneyProvenanceTooltip, sourceClassForMoneyProvenance } from '../source-state.js';

const visibleRowKeys = (record) => Object.keys(record || {}).filter((key) =>
  !key.startsWith('__') &&
  !['fx', 'moneyProvenance', 'sourceLineage', 'proofPosture'].includes(key)
);

/* MoneyCell — every peso amount in the app renders here.
   v8 — Popover now uses Floating UI (no more clipping bug).
   v8 perf — React.memo on the default export (end of file). MoneyCell
   renders hundreds of times per multi-period table; memoizing skips the
   whole tree when props haven't changed. */
function MoneyCellImpl({
  value, breakdown, term, registryKey, onJump,
  short = false, decimals = 2, align = 'right', tone,
  label, row, scenario, fxHint, moneyProvenance, zohoRef,
}) {
  /* Drill-downs / EduTips are gated via Settings → Guide Controls.
     When disabled we still render the bare money value — just without
     the click-to-popover affordance. */
  const { drillDowns, eduTips } = useGuides();
  const hasDrill = drillDowns && Array.isArray(breakdown) && breakdown.length > 0;
  const hasEdu = eduTips && !!term && !!GLOSSARY[term];
  const refs = registryKey && drillDowns ? lookupRegistry(registryKey) : [];
  const hasRefs = refs.length > 0;
  /* v8 Path V — drop the leading-minus redundancy when a tone is set.
     If the cell is already colored good/warn/bad, the color already
     communicates the directional meaning; layering a minus sign on
     top of red (or green) is counter-intuitive. Formal/Comparative
     tables still use parens on neutral-colored rows; tone-colored
     cells show the absolute magnitude with only the color as signal. */
  const showAbsolute = !!tone && typeof value === 'number' && value < 0;
  const renderValue = showAbsolute ? Math.abs(value) : value;
  const display = short
    ? fmtMoney(renderValue, { decimals: 2 })
    : fmtMoney(renderValue, { decimals });

  const [ctx, setCtx] = useState({ open: false, x: 0, y: 0 });
  const [explainOpen, setExplainOpen] = useState(false);
  const toast = useToast();
  const resolvedMoneyProvenance = moneyProvenance
    || row?.moneyProvenance
    || row?.__moneyProvenance
    || row?.fx
    || row?.__fx
    || null;
  const moneyProvenanceClass = sourceClassForMoneyProvenance(resolvedMoneyProvenance);
  const moneyProvenanceTip = resolvedMoneyProvenance
    ? formatMoneyProvenanceTooltip(resolvedMoneyProvenance)
    : null;
  const resolvedFxHint = fxHint || (
    resolvedMoneyProvenance?.originCurrency &&
    resolvedMoneyProvenance?.exchangeRate &&
    resolvedMoneyProvenance?.rateResolution !== 'SAME_CURRENCY'
      ? {
          native: resolvedMoneyProvenance.originCurrency,
          rate: resolvedMoneyProvenance.exchangeRate,
          title: moneyProvenanceTip,
        }
      : null
  );

  const copyText = useCallback((text, label) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast(`Copied ${label}`),
        () => toast({ message: 'Copy failed', tone: 'error' }),
      );
    } else {
      toast({ message: 'Copy unavailable', tone: 'warn' });
    }
  }, [toast]);

  const exportRowAsCSV = useCallback(() => {
    if (!row) { toast({ message: 'No row data', tone: 'warn' }); return; }
    const cols = visibleRowKeys(row);
    const header = cols.join(',');
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    };
    const line = cols.map(k => escape(row[k])).join(',');
    copyText(`${header}\n${line}`, 'local visible-row CSV');
  }, [row, copyText, toast]);

  const onContextMenu = useCallback((e) => {
    e.preventDefault();
    setCtx({ open: true, x: e.clientX, y: e.clientY });
  }, []);

  const closeCtx = useCallback(() => setCtx({ open: false, x: 0, y: 0 }), []);

  const menuItems = [
    {
      id: 'copy-money', label: 'Copy amount',
      hint: fmtMoney(value, { decimals: 2 }),
      onSelect: () => copyText(fmtMoney(value, { decimals: 2 }), 'money amount'),
    },
    {
      id: 'copy-num', label: 'Copy as number',
      hint: String(value ?? ''),
      onSelect: () => copyText(String(value ?? ''), 'raw number'),
    },
    ...(hasRefs && onJump ? [{
      id: 'drill-into', label: 'Drill into →',
      hint: refs[0].tab.toUpperCase() + ' · ' + refs[0].metric,
      onSelect: () => onJump(refs[0]),
    }] : []),
    {
      id: 'export-csv', label: 'Copy local visible-row CSV',
      hint: row ? `${visibleRowKeys(row).length} visible fields; review/redact before sharing` : 'no row context',
      disabled: !row,
      onSelect: exportRowAsCSV,
    },
    {
      id: 'explain', label: 'Explain this number',
      hint: 'open formula + breakdown',
      onSelect: () => setExplainOpen(true),
    },
  ];

  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const toneClass = tone === 'good' ? 'text-[#4ade80]' : tone === 'warn' ? 'text-[#fbbf24]' : tone === 'bad' ? 'text-[#f87171]' : '';
  const baseClass = `font-mono whitespace-nowrap ${alignClass} ${toneClass}`;
  const linkClass = 'cursor-help border-b border-dotted border-[rgba(148,163,184,0.5)] pb-px hover:!border-accent-blue hover:!text-[#93c5fd]';

  const fxHintEl = resolvedFxHint && (
    <em className="inline-block ml-[0.4rem] text-text-dim text-[0.72rem] not-italic font-mono" title={resolvedFxHint.title || `Native ${resolvedFxHint.native} @ ${resolvedFxHint.rate}`}>
      {resolvedFxHint.native} @ {resolvedFxHint.rate}
    </em>
  );
  const moneyProvenanceChip = moneyProvenanceClass && (
    <span className="inline-flex ml-[0.3rem] align-middle">
      <ProvenanceChip
        kind={{
          sourceClass: moneyProvenanceClass,
          label: 'Money provenance',
          compactLabel: resolvedMoneyProvenance?.providerMode || resolvedMoneyProvenance?.rateSource || 'money',
        }}
        tooltip={moneyProvenanceTip}
        compact
      />
    </span>
  );

  if (!hasDrill && !hasEdu && !hasRefs) {
    return (
      <>
        <span
          className={baseClass}
          onContextMenu={onContextMenu}
        >
          {display}
          {fxHintEl}
          {moneyProvenanceChip}
        </span>
        <ContextMenu open={ctx.open} x={ctx.x} y={ctx.y} items={menuItems} onClose={closeCtx} />
        <ExplainModal
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          value={value}
          label={label}
          breakdown={breakdown}
          term={term}
          refs={refs}
          scenario={scenario}
          zohoRef={zohoRef}
        />
      </>
    );
  }

  const total = hasDrill ? breakdown.reduce((s, r) => s + (r.amount || 0), 0) : value;
  const content = (
    <div className="flex flex-col gap-2 min-w-[280px]">
      {hasDrill && (
        <>
          <div className="flex justify-between items-baseline pb-[6px] border-b border-[#334155] text-[11px] uppercase tracking-[0.06em] text-[#94a3b8]">
            <span>Breakdown</span>
            <strong className="text-[14px] text-[#f1f5f9] tracking-normal normal-case">{fmtMoney(total, { decimals: 2 })}</strong>
          </div>
          <div className="flex flex-col gap-1 max-h-[260px] overflow-y-auto">
            {breakdown.map((r, i) => {
              const pct = total > 0 ? (r.amount / total) : 0;
              return (
                <div key={i} className="grid grid-cols-[1fr_80px_70px] gap-2 items-center text-[11px]">
                  <div className="flex flex-col min-w-0">
                    <span className="text-[#e2e8f0] overflow-hidden text-ellipsis whitespace-nowrap">{r.label}</span>
                    {r.source && <em className="text-[#64748b] italic text-[10px]">{r.source}</em>}
                  </div>
                  <div className="relative h-[6px] bg-[rgba(148,163,184,0.15)] rounded-[3px] overflow-hidden">
                    <div
                      className="absolute top-0 left-0 bottom-0 rounded-[3px] bg-gradient-to-r from-[#60a5fa] to-[#c084fc]"
                      style={{ width: `${Math.max(0, pct * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <div className="text-[#f1f5f9] font-mono text-right font-semibold">{fmtMoney(r.amount, { decimals: 2 })}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {hasEdu && (
        <div className="mt-1 pt-2 border-t border-[#334155]">
          <div className="flex items-baseline gap-2 flex-wrap">
            <strong className="text-[#fbbf24]">{GLOSSARY[term].term}</strong>
            <code className="bg-[rgba(251,191,36,0.1)] text-[#fbbf24] px-[6px] py-[2px] rounded-[4px] text-[11px]">{GLOSSARY[term].formula || ''}</code>
          </div>
          <p className="mt-1 text-[#cbd5e1] text-[11px] leading-[1.5]">{GLOSSARY[term].long || GLOSSARY[term].short}</p>
        </div>
      )}
      {hasRefs && (
        <div className="mt-[10px] pt-[10px] border-t border-dashed border-border">
          <div className="text-[10.5px] tracking-[0.08em] uppercase text-text-muted mb-[6px]">Used in</div>
          <ul className="list-none p-0 m-0 flex flex-col gap-1">
            {refs.map((r, i) => (
              <li key={i}>
                {onJump ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.02] border border-border text-[11.5px] text-text cursor-pointer transition-colors hover:bg-[rgba(251,191,36,0.08)] hover:border-[rgba(251,191,36,0.4)]"
                    onClick={() => onJump(r)}
                  >
                    <span className="text-[10px] font-bold px-[6px] py-px rounded-[4px] bg-white/[0.06] text-[#fbbf24] tracking-[0.06em]">{r.tab.toUpperCase()}</span>
                    <span className="text-text-muted">→</span>
                    <span className="text-text">{r.metric}</span>
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.02] border border-border text-[11.5px] text-text">
                    <span className="text-[10px] font-bold px-[6px] py-px rounded-[4px] bg-white/[0.06] text-[#fbbf24] tracking-[0.06em]">{r.tab.toUpperCase()}</span>
                    <span className="text-text-muted">→</span>
                    <span className="text-text">{r.metric}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Popover content={content} maxWidth={420}>
        <span
          className={`${baseClass} ${linkClass}`}
          onContextMenu={onContextMenu}
        >
          {display}
          {fxHintEl}
          {moneyProvenanceChip}
          <span className="inline-block text-[#64748b] ml-1 font-bold opacity-70" aria-hidden>·</span>
        </span>
      </Popover>
      <ContextMenu open={ctx.open} x={ctx.x} y={ctx.y} items={menuItems} onClose={closeCtx} />
      <ExplainModal
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        value={value}
        label={label}
        breakdown={breakdown}
        term={term}
        refs={refs}
      />
    </>
  );
}

/* Memoized — skip re-render when value + formatting props are unchanged.
   `row`, `scenario`, `onJump`, `breakdown`, `refs` are passed by reference
   and usually stable per-tab. Shallow equality is sufficient. */
const MoneyCell = memo(MoneyCellImpl);
export default MoneyCell;
